import { API_ORIGIN } from '../config';

// The staff login token. authService stores it here at login and removes it at logout.
const TOKEN_KEY = 'authToken';

// Fired when the API rejects the stored token (expired or revoked). AuthContext signs the user out.
export const AUTH_EXPIRED_EVENT = 'auth:expired';

// Calls that handle a rejected token themselves, or never need one.
const SELF_HANDLED = ['/api/login', '/api/logout', '/api/register', '/api/forgot-password', '/api/reset-password', '/api/verify-token'];

export const getAuthToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

const apiPrefix = `${API_ORIGIN.replace(/\/+$/, '')}/`;

const urlOf = (input) => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input?.url || '';
};

const isApiUrl = (url) => url.startsWith(apiPrefix);
const isSelfHandled = (url) => {
  const path = url.slice(apiPrefix.length - 1).split('?')[0];
  return SELF_HANDLED.includes(path);
};

/**
 * The backend requires the staff login on every employee endpoint. It accepts the JWT as
 * `Authorization: Bearer <token>`, falling back to the httpOnly login cookie.
 *
 * Rather than repeat that in each of the app's ~250 fetch calls, this wraps window.fetch once:
 * every request to API_ORIGIN gets the stored token (unless the call already sets its own
 * Authorization header, as the client-admin portal does with its own token) and sends cookies.
 * Requests to any other origin are untouched. Public endpoints ignore the token.
 */
export function installApiAuth() {
  if (typeof window === 'undefined' || window.__festivalsApiAuth) return;
  window.__festivalsApiAuth = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = urlOf(input);
    if (!isApiUrl(url)) return nativeFetch(input, init);

    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    const token = getAuthToken();
    const attachedStoredToken = Boolean(token) && !headers.has('Authorization');
    if (attachedStoredToken) headers.set('Authorization', `Bearer ${token}`);

    const response = await nativeFetch(input, {
      ...init,
      headers,
      credentials: init.credentials || 'include',
    });

    if (response.status === 401 && attachedStoredToken && !isSelfHandled(url)) {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }
    return withMessageField(response);
  };
}

/**
 * The backend reports errors as `{ success: false, error }`; most of this app was written
 * against the old backend and reads `message`. Copy `error` into `message` on failed JSON
 * responses so those screens show the real reason instead of a generic fallback.
 */
async function withMessageField(response) {
  if (response.ok || !(response.headers.get('content-type') || '').includes('application/json')) {
    return response;
  }
  try {
    const body = await response.clone().json();
    if (!body || typeof body !== 'object' || !body.error || body.message) return response;
    return new Response(JSON.stringify({ ...body, message: body.error }), {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
  } catch (e) {
    return response;
  }
}

/**
 * Open a protected API file (PDF, image, video) in a new tab. A plain link or window.open can't
 * carry the login, so the file is fetched with it and shown from a local object URL.
 * The tab is opened first, inside the click, so popup blockers allow it.
 */
export async function openApiFile(url, { download = false, filename } = {}) {
  const tab = download ? null : window.open('', '_blank');
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(response.status === 404 ? 'File not found' : `Could not open the file (${response.status})`);
    }
    const objectUrl = URL.createObjectURL(await response.blob());
    if (download) {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || '';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else if (tab) {
      tab.location.href = objectUrl;
    } else {
      window.open(objectUrl, '_blank');
    }
    // Keep the object URL alive long enough for the tab or download to read it.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (error) {
    if (tab) tab.close();
    throw error;
  }
}
